import { useEffect, useState } from "react";
import { X, Check } from "lucide-react";
import { waLink } from "@/siteConfig";
import { Eyebrow } from "@/components/bits";
import { useLang, STRINGS } from "@/i18n";

const WHO_OPTIONS = ["lc.who.owner", "lc.who.developer", "lc.who.acquirer"];

const WHO_KEYS = {
  owner: "lc.who.owner",
  developer: "lc.who.developer",
  acquirer: "lc.who.acquirer",
};

const WHAT_OPTIONS = {
  "lc.who.owner": ["opt.asset.beach", "opt.asset.farm", "opt.asset.hotel", "opt.asset.inst"],
  "lc.who.developer": ["lc.what.demo", "lc.what.sales", "lc.what.channel"],
  "lc.who.acquirer": ["opt.app.land", "opt.app.hosp", "opt.app.health"],
};

const BANDS = ["opt.band.1", "opt.band.2", "opt.band.3", "opt.band.4"];
const LOCATIONS = ["loc.puri", "loc.bbsr", "loc.ctc", "loc.other"];

const Field = ({ label, error, reqLabel, children }) => (
  <div>
    <span
      className={`font-mono-label text-[10px] tracking-[0.25em] uppercase transition-colors duration-300 ${
        error ? "text-[#B8894B]" : "text-[#EDE7DA]/45"
      }`}
    >
      {label}
      {error ? ` ${reqLabel}` : ""}
    </span>
    <div className="mt-2">{children}</div>
  </div>
);

export const LeadCaptureForm = ({ lockedWho }) => {
  const { t } = useLang();
  const [who, setWho] = useState(lockedWho || "");
  const [what, setWhat] = useState("");
  const [band, setBand] = useState("");
  const [units, setUnits] = useState("");
  const [location, setLocation] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [sent, setSent] = useState(false);

  const isDeveloper = who === "lc.who.developer";
  const capacity = isDeveloper ? units.trim() : band;

  const missing = {
    name: !name.trim(),
    phone: !phone.trim(),
    who: !who,
    what: !what,
    capacity: !capacity,
    location: !location,
  };
  const err = (k) => attempted && missing[k];

  const lines = [
    t("wa.lc.title"),
    "—",
    `${t("wa.name")}: ${name.trim()}`,
    `${t("wa.phone")}: ${phone.trim()}`,
    `${t("wa.who")}: ${t(who)}`,
    `${t("wa.interest")}: ${t(what)}`,
    `${t("wa.capacity")}: ${isDeveloper ? `${units.trim()} ${t("wa.units")}` : t(band)}`,
    `${t("wa.location")}: ${t(location)}`,
  ];
  if (notes.trim()) lines.push(`${t("wa.notes")}: ${notes.trim()}`);
  const url = waLink(lines.join("\n"));

  const selectCls = (error) =>
    `w-full bg-[#0E1613] border px-4 py-3 text-sm font-light outline-none transition-colors duration-300 ${
      error
        ? "border-[#B8894B] text-[#B8894B]"
        : "border-[#1A2A24] focus:border-[#B8894B] text-[#EDE7DA]"
    }`;

  const lineCls = (error) =>
    `w-full bg-transparent border-b outline-none py-2.5 text-[#EDE7DA] placeholder-[#EDE7DA]/25 transition-colors duration-300 ${
      error ? "border-[#B8894B]" : "border-[#1A2A24] focus:border-[#B8894B]"
    }`;

  const submit = () => {
    setAttempted(true);
    if (Object.values(missing).some(Boolean)) return;
    window.open(url, "_blank");
    setSent(true);
  };

  if (sent) {
    return (
      <div data-testid="lead-form-confirmation" className="py-4">
        <span className="inline-flex items-center justify-center w-11 h-11 border border-[#B8894B]">
          <Check className="w-5 h-5 text-[#B8894B]" />
        </span>
        <h3 className="font-display text-2xl text-[#EDE7DA] tracking-tight mt-6">
          {t("lc.done.h")}
        </h3>
        <p className="font-light text-sm text-[#EDE7DA]/60 leading-relaxed mt-3">
          {t("lc.done.body")}
        </p>
        <a
          data-testid="lead-form-fallback-link"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-7 font-mono-label text-[11px] tracking-[0.2em] uppercase text-[#B8894B] border-b border-[#B8894B]/40 pb-1 hover:text-[#EDE7DA] hover:border-[#EDE7DA]/40 transition-colors duration-300"
        >
          {t("lc.done.fallback")}
        </a>
      </div>
    );
  }

  return (
    <div className="grid gap-7 mt-8">
      <div className="grid sm:grid-cols-2 gap-7">
        <Field label={t("lc.nameLabel")} error={err("name")} reqLabel={t("lc.required")}>
          <input
            data-testid="lead-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("ph.name")}
            className={lineCls(err("name"))}
          />
        </Field>
        <Field label={t("lc.phoneLabel")} error={err("phone")} reqLabel={t("lc.required")}>
          <input
            data-testid="lead-phone-input"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+91"
            className={lineCls(err("phone"))}
          />
        </Field>
      </div>

      <Field label={t("lc.whoLabel")} error={err("who")} reqLabel={t("lc.required")}>
        {lockedWho ? (
          <p
            data-testid="lead-who-locked"
            className="font-mono-label text-[11px] tracking-[0.2em] uppercase text-[#B8894B] border border-[#B8894B]/50 bg-[#1A2A24] px-4 py-3"
          >
            {t(lockedWho)}
          </p>
        ) : (
          <select
            data-testid="lead-who-select"
            value={who}
            onChange={(e) => {
              setWho(e.target.value);
              setWhat("");
              setBand("");
              setUnits("");
            }}
            className={selectCls(err("who"))}
          >
            <option value="" disabled>
              {t("lc.selectPh")}
            </option>
            {WHO_OPTIONS.map((k) => (
              <option key={k} value={k}>
                {t(k)}
              </option>
            ))}
          </select>
        )}
      </Field>

      {who && (
        <Field label={t("lc.whatLabel")} error={err("what")} reqLabel={t("lc.required")}>
          <select
            data-testid="lead-what-select"
            value={what}
            onChange={(e) => setWhat(e.target.value)}
            className={selectCls(err("what"))}
          >
            <option value="" disabled>
              {t("lc.selectPh")}
            </option>
            {WHAT_OPTIONS[who].map((k) => (
              <option key={k} value={k}>
                {t(k)}
              </option>
            ))}
          </select>
        </Field>
      )}

      {who && (
        <div className="grid sm:grid-cols-2 gap-7">
          <Field
            label={isDeveloper ? t("lc.unitsLabel") : t("lc.bandLabel")}
            error={err("capacity")}
            reqLabel={t("lc.required")}
          >
            {isDeveloper ? (
              <input
                data-testid="lead-units-input"
                value={units}
                onChange={(e) => setUnits(e.target.value)}
                placeholder={t("ph.units")}
                className={lineCls(err("capacity"))}
              />
            ) : (
              <select
                data-testid="lead-band-select"
                value={band}
                onChange={(e) => setBand(e.target.value)}
                className={selectCls(err("capacity"))}
              >
                <option value="" disabled>
                  {t("lc.selectPh")}
                </option>
                {BANDS.map((k) => (
                  <option key={k} value={k}>
                    {t(k)}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label={t("lc.locLabel")} error={err("location")} reqLabel={t("lc.required")}>
            <select
              data-testid="lead-location-select"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className={selectCls(err("location"))}
            >
              <option value="" disabled>
                {t("lc.selectPh")}
              </option>
              {LOCATIONS.map((k) => (
                <option key={k} value={k}>
                  {t(k)}
                </option>
              ))}
            </select>
          </Field>
        </div>
      )}

      <Field label={t("lc.notesLabel")}>
        <textarea
          data-testid="lead-notes-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t("lc.notesPh")}
          rows={3}
          className="w-full bg-transparent border border-[#1A2A24] focus:border-[#B8894B] outline-none p-4 text-sm text-[#EDE7DA] placeholder-[#EDE7DA]/25 transition-colors duration-300 resize-none"
        />
      </Field>

      <div>
        <button
          type="button"
          data-testid="lead-form-submit"
          onClick={submit}
          className="w-full font-mono-label text-xs tracking-[0.2em] uppercase bg-[#B8894B] text-[#0E1613] px-7 py-4 hover:bg-[#EDE7DA] transition-colors duration-300"
        >
          {t("lc.submit")}
        </button>
        <p className="font-mono-label text-[10px] tracking-[0.15em] uppercase text-[#EDE7DA]/40 mt-4 text-center">
          {t("cta.waNote")}
        </p>
      </div>
    </div>
  );
};

export const LeadCaptureModal = ({ door, onClose }) => {
  const { setLang, t } = useLang();
  const locked = WHO_KEYS[door] || null;
  const [langStep, setLangStep] = useState(false);

  useEffect(() => {
    if (door) setLangStep(!WHO_KEYS[door]);
  }, [door]);

  if (!door) return null;

  const pick = (l) => {
    setLang(l);
    setLangStep(false);
  };

  return (
    <div
      data-testid="lead-capture-modal"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
    >
      <div
        data-testid="lead-modal-backdrop"
        className="absolute inset-0 bg-[#0E1613]/85 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-[#131F1A] border border-[#1A2A24] w-full sm:max-w-xl max-h-[92vh] overflow-y-auto p-7 md:p-10 sm:m-6">
        <button
          type="button"
          data-testid="lead-modal-close"
          onClick={onClose}
          className="absolute top-5 right-5 text-[#EDE7DA]/50 hover:text-[#B8894B] transition-colors duration-300"
        >
          <X className="w-5 h-5" />
        </button>

        {langStep ? (
          <div data-testid="lang-step" className="py-4">
            <img src="/logo-mark.png" alt="Novare Estates" className="w-12 h-12 mb-8" />
            <Eyebrow>Novare Estates</Eyebrow>
            <h3 className="font-display text-2xl md:text-3xl text-[#EDE7DA] tracking-tight mt-4">
              {STRINGS["lang.title"].en}
            </h3>
            <p
              className="text-xl md:text-2xl text-[#EDE7DA]/60 mt-2"
              style={{ fontFamily: "'Noto Sans Oriya', sans-serif" }}
            >
              {STRINGS["lang.title"].or}
            </p>
            <div className="grid grid-cols-2 gap-3 mt-10">
              <button
                type="button"
                data-testid="lang-btn-en"
                onClick={() => pick("en")}
                className="border border-[#1A2A24] hover:border-[#B8894B] py-8 font-display text-2xl text-[#EDE7DA] hover:text-[#B8894B] transition-colors duration-300"
              >
                English
              </button>
              <button
                type="button"
                data-testid="lang-btn-or"
                onClick={() => pick("or")}
                className="border border-[#1A2A24] hover:border-[#B8894B] py-8 text-2xl text-[#EDE7DA] hover:text-[#B8894B] transition-colors duration-300"
                style={{ fontFamily: "'Noto Sans Oriya', sans-serif" }}
              >
                ଓଡ଼ିଆ
              </button>
            </div>
          </div>
        ) : (
          <>
            <img src="/logo-mark.png" alt="Novare Estates" className="w-10 h-10 mb-6" />
            <Eyebrow>{t("lc.eyebrow")}</Eyebrow>
            <h3 className="font-display text-2xl md:text-3xl text-[#EDE7DA] tracking-tight mt-4">
              {t("lc.h")}
            </h3>
            <p className="font-light text-sm text-[#EDE7DA]/55 leading-relaxed mt-3">
              {t("lc.sub")}
            </p>
            <LeadCaptureForm key={locked || "open"} lockedWho={locked} />
          </>
        )}
      </div>
    </div>
  );
};
